import clsx from "clsx";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import type { WizardPartner } from "../useCreateProgramWizard";

import { Button as ButtonV2 } from "../../../../Button";
import { Typo } from "../../../../Typo";
import { WizardFooter } from "../WizardFooter";
import { computeSplitDollarValues, hasEnteredSplit } from "./splitDollarValues";

type SplitStepProps = {
  title: string;
  stepLabel?: string;
  partners: WizardPartner[];
  split: number;
  setSplit: (n: number) => void;
  split2: number;
  setSplit2: (n: number) => void;
  masterShare: number;
  onBack: () => void;
  onContinue: () => void;
  /**
   * The rebate-split-override affordance (link or summary card). Rendered after
   * the split block, at the step's own spacing — the canvas keeps it outside
   * `.split3`. Optional so callers that don't offer overrides simply omit it.
   */
  overrideSlot?: React.ReactNode;
  isMobile?: boolean;
  footerContainer?: Element | null;
};

const clampPct = (raw: string): number => {
  const digits = raw.replace(/[^\d]/g, "");
  if (digits === "") return 0;
  return Math.max(0, Math.min(100, parseInt(digits, 10)));
};

export const SplitStep = ({
  title,
  stepLabel,
  partners,
  split,
  setSplit,
  split2,
  setSplit2,
  masterShare,
  onBack,
  onContinue,
  overrideSlot,
  isMobile,
  footerContainer,
}: SplitStepProps) => {
  const { t } = useTranslation();
  const isTwoSub = partners.length > 1;
  const over = masterShare < 0;
  const entered = hasEnteredSplit({ split, split2, isTwoSub });
  const canContinue = !over && entered;

  const firstInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    firstInputRef.current?.focus({ preventScroll: true });
  }, []);

  const { dollarSub, dollarSub2, dollarMaster } = computeSplitDollarValues({
    split,
    split2,
    masterShare,
    isTwoSub,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="flex flex-col gap-3">
        {stepLabel && (
          <Typo
            element="span"
            type="labelXS"
            textColor="text-text-tertiary"
            className="uppercase tracking-[0.04em]"
          >
            {stepLabel}
          </Typo>
        )}
        <Typo element="h2" type={isMobile ? "headingLG" : "headingXL"}>
          {title}
        </Typo>
        <Typo element="p" type="bodySM" textColor="text-text-secondary">
          {/* Only the 1-Sub subtitle needs the sentence-ending period supplied
              here — the 2-Sub string carries its own, exactly as the canvas has
              them. */}
          {isTwoSub
            ? t(
                "subIBReport_splitSubtitleMulti",
                "Manage how much of your rebate earnings you will share.",
              )
            : `${t(
                "subIBReport_splitSubtitle",
                "Manage how much of your rebate earnings you will share",
              )}.`}{" "}
          <Typo element="span" type="labelSM" textColor="text-text-secondary">
            {t(
              "subIBReport_splitLockNote",
              "Once the program is created, you cannot change the rebate split.",
            )}
          </Typo>
        </Typo>
      </div>

      {/* Design change #8 (PART-3930): the split block — distribution bar →
          split-percentage inputs → hypothetical caption — carries a uniform
          16px vertical rhythm (`.split3` in the design, previously 24/12px).
          The step's own header→block and block→footer spacing stays at the
          outer 24px, so this needs its own wrapper rather than retuning the
          root gap. Serves the 1-Sub and 2-Sub views from one wrapper. */}
      <div className="flex flex-col gap-4">
        {/* Every Sub IB keeps a visible stub in the bar even at 0%: a 4px
            floor plus the 2px gap, so their colour is always readable
            (design QA). `min-w-[4px]` rather than a width floor because the
            segments are flex items — the percentage is the flex basis, and
            the min-width is what stops the browser shrinking them away. The
            master segment absorbs the difference.

            `masterShare` is the one segment that may legitimately vanish: it
            is rendered only when positive, both because "You receive" has no
            stub to preserve and because an over-allocated split drives it
            negative, where a zero-width item would still reserve its gap. */}
        <div className="flex h-4 gap-0.5 overflow-hidden rounded-full">
          <div
            className="min-w-[4px] bg-surface-emphasis"
            style={{ width: `${Math.max(0, split)}%` }}
          />
          {isTwoSub && (
            <div
              className="min-w-[4px] bg-surface-accent-blue-subtler"
              style={{ width: `${Math.max(0, split2)}%` }}
            />
          )}
          {masterShare > 0 && (
            <div
              className="bg-surface-brand-default"
              style={{ width: `${masterShare}%` }}
            />
          )}
        </div>

        {/* 8px between split blocks — `.split3__inputs` inputs and
          `.split-cards` preview cards in the design, both previously 12px.
          Our grid holds the percentage inputs and the "You receive" preview
          card together, so this single gap covers both of the design's rows. */}
        <div
          className={clsx(
            "grid gap-2",
            isMobile ? "grid-cols-1" : "grid-cols-2",
          )}
        >
          <div className="flex flex-col gap-2 rounded-lg border-2 border-transparent bg-surface-tonal-default p-4 focus-within:border-surface-brand-default">
            <label
              htmlFor="wiz-split-input"
              className="flex items-center gap-1.5 text-text-secondary"
            >
              <span className="h-3.5 w-3.5 shrink-0 rounded bg-surface-emphasis" />
              <Typo
                element="span"
                type="bodyXS"
                textColor="text-text-secondary"
              >
                {/* Generic in the 1-Sub view (canvas `wiz.split.subReceives`)
                    — there is nobody to distinguish them from. */}
                {isTwoSub
                  ? t("subIBReport_splitReceivesLabel", "{{name}} receives", {
                      name:
                        partners[0]?.displayName ??
                        t("subIBReport_defaultSubIb", "Sub IB"),
                    })
                  : t("subIBReport_splitSubReceives", "Sub IB receives")}
              </Typo>
            </label>
            <div className="flex items-baseline justify-between gap-2">
              <input
                ref={firstInputRef}
                id="wiz-split-input"
                type="text"
                inputMode="numeric"
                value={split === 0 ? "" : String(split)}
                placeholder={t("subIBReport_splitMinPlaceholder", "Min 0")}
                onChange={(e) => setSplit(clampPct(e.target.value))}
                className="min-w-0 flex-1 bg-transparent font-sans text-xl font-semibold leading-[1.2] text-text-primary outline-none placeholder:text-text-tertiary"
                aria-label={t(
                  "subIBReport_splitReceivesAria",
                  "{{name}} receives percentage",
                  {
                    name: partners[0]?.displayName,
                  },
                )}
              />
              <Typo element="span" type="labelLG">
                %
              </Typo>
            </div>
          </div>

          {isTwoSub && (
            <div className="flex flex-col gap-2 rounded-lg border-2 border-transparent bg-surface-tonal-default p-4 focus-within:border-surface-brand-default">
              <label
                htmlFor="wiz-split-input-2"
                className="flex items-center gap-1.5 text-text-secondary"
              >
                <span className="h-3.5 w-3.5 shrink-0 rounded bg-surface-accent-blue-subtler" />
                <Typo
                  element="span"
                  type="bodyXS"
                  textColor="text-text-secondary"
                >
                  {t("subIBReport_splitReceivesLabel", "{{name}} receives", {
                    name:
                      partners[1]?.displayName ??
                      t("subIBReport_defaultSubIb", "Sub IB"),
                  })}
                </Typo>
              </label>
              <div className="flex items-baseline justify-between gap-2">
                <input
                  id="wiz-split-input-2"
                  type="text"
                  inputMode="numeric"
                  value={split2 === 0 ? "" : String(split2)}
                  placeholder={t("subIBReport_splitMinPlaceholder", "Min. 0")}
                  onChange={(e) => setSplit2(clampPct(e.target.value))}
                  className="min-w-0 flex-1 bg-transparent font-sans text-xl font-semibold leading-[1.2] text-text-primary outline-none placeholder:text-text-tertiary"
                  aria-label={t(
                    "subIBReport_splitReceivesAria",
                    "{{name}} receives percentage",
                    {
                      name: partners[1]?.displayName,
                    },
                  )}
                />
                <Typo element="span" type="labelLG">
                  %
                </Typo>
              </div>
            </div>
          )}

          {/* With two Sub IBs the two inputs fill the 2-column row, so this
              card would otherwise sit half-width in the row below. It spans
              both columns instead (design QA). Only on the 2-column grid:
              mobile is `grid-cols-1`, where a span would create an implicit
              second column. */}
          <div
            className={clsx(
              "flex flex-col gap-2 rounded-lg bg-surface-brand-subtle p-4",
              isTwoSub && !isMobile && "col-span-2",
            )}
          >
            <span className="flex items-center gap-1.5 text-text-brand-default">
              <span className="h-3.5 w-3.5 shrink-0 rounded bg-surface-brand-default" />
              <Typo
                element="span"
                type="bodyXS"
                textColor="text-text-brand-default"
              >
                {t("subIBReport_splitYouReceive", "You receive")}
              </Typo>
            </span>
            <div className="flex items-baseline justify-between gap-2">
              <Typo
                element="span"
                type="labelLG"
                textColor="text-text-brand-default"
              >
                {over || !entered ? "--" : masterShare}
              </Typo>
              <Typo
                element="span"
                type="labelLG"
                textColor="text-text-brand-default"
              >
                %
              </Typo>
            </div>
          </div>
        </div>

        {over ? (
          <Typo element="p" type="bodySM" textColor="text-text-error">
            {t(
              "subIBReport_splitOverError",
              "Sub IB shares can't exceed 100%.",
            )}
          </Typo>
        ) : (
          <Typo element="p" type="bodySM" textColor="text-text-secondary">
            {isTwoSub
              ? t(
                  "subIBReport_splitCaptionTwoSub",
                  "Example: If you earn a $1,000 rebate, you would pay {{dollarSub}} to {{name1}}, {{dollarSub2}} to {{name2}}, and keep {{dollarMaster}}.",
                  {
                    dollarSub,
                    dollarSub2,
                    dollarMaster,
                    name1:
                      partners[0]?.displayName ??
                      t("subIBReport_defaultSubIb", "Sub IB"),
                    name2:
                      partners[1]?.displayName ??
                      t("subIBReport_defaultSubIb", "Sub IB"),
                  },
                )
              : t(
                  "subIBReport_splitCaption",
                  "Example: If you earn a $1,000 rebate, you would pay {{dollarSub}} to the Sub IB and keep {{dollarMaster}}.",
                  { dollarSub, dollarMaster },
                )}
          </Typo>
        )}
      </div>

      {overrideSlot}

      {/* TODO: remove IIFE per "No IIFEs in JSX" (AGENTS.md) — pick basic/medium/complex tier */}
      {(() => {
        const footer = (
          <WizardFooter isMobile={isMobile}>
            <ButtonV2 variant="tonal" size="md" onClick={onBack}>
              {t("common_back", "Back")}
            </ButtonV2>
            <ButtonV2
              variant="brand"
              size="md"
              disabled={!canContinue}
              onClick={onContinue}
            >
              {t("common_next", "Next")}
            </ButtonV2>
          </WizardFooter>
        );
        return footerContainer ? createPortal(footer, footerContainer) : footer;
      })()}
    </div>
  );
};
